/* eslint-disable camelcase */
import { CUSTOM_SEGMENT_KEY_PREFIX } from "@/modules/Presets/TargetingExpression/targetingExpression.constant";
import { targetExpressionModuleDefaultValues } from "./data";
import { CtvChannel } from "./CreateOrEdit/SubModules/CtvChannels/ctvChannels.interface";

interface FilterValue {
  value: string | number;
}

// Helper function to map filter values
function mapForFilterValues(
  data: FilterValue[] | null | undefined
): (string | number)[] {
  return data ? data.map((item) => item.value) : [];
}
function mapForFilterValuesAndParseAsString(
  data: FilterValue[] | null | undefined
): (string | number)[] {
  return data ? data.map((item) => String(item.value)) : [];
}
// Helper function to get values for reverse mapping
function valueForReverseMap(
  data: Record<string, any> | null | undefined,
  module: string,
  filter: string,
  type: "include" | "exclude" | "require"
) {
  if (!data) return null;
  if (type === "require") return data?.[module]?.all?.[filter]?.all;
  if (type === "include") return data?.[module]?.all?.[filter]?.any;
  if (type === "exclude") return data?.[module]?.none?.[filter]?.any;
}

// Helper function for include and exclude filters
function valueForIncludeAndExcludeFilter(
  data: Record<string, any> | null | undefined,
  module: string,
  filter: string
) {
  return {
    included: mapForFilterValues(
      valueForReverseMap(data, module, filter, "include")
    ),
    excluded: mapForFilterValues(
      valueForReverseMap(data, module, filter, "exclude")
    ),
  };
}

// Helper function for include or exclude filters
function valueForIncludeOrExcludeFilter<T = (string | number)[]>(
  data: Record<string, any> | null | undefined,
  module: string,
  filter: string,
  mapFunction: (
    data: FilterValue[] | null | undefined
  ) => T = mapForFilterValues as any
): { value: T; type: string | null } {
  if (data?.[module]?.all?.[filter]?.all?.length) {
    return {
      value: mapFunction(valueForReverseMap(data, module, filter, "require")),
      type: "require",
    };
  }
  if (data?.[module]?.all?.[filter]?.any?.length) {
    return {
      value: mapFunction(valueForReverseMap(data, module, filter, "include")),
      type: "include",
    };
  }
  if (data?.[module]?.none?.[filter]?.any?.length) {
    return {
      value: mapFunction(valueForReverseMap(data, module, filter, "exclude")),
      type: "exclude",
    };
  }

  return {
    value: [] as unknown as T,
    type: null,
  };
}

interface TimeResponseField {
  value: [number, number];
}

interface TimeResponseResult {
  startDay: number;
  endDay: number;
  startTime: Date;
  endTime: Date;
}

// Time response mapper
export function timeResponseMapper(
  response: TimeResponseField[]
): TimeResponseResult[] {
  const result = [];

  for (const field of response) {
    const startMins = field?.value[0];
    const endMins = field?.value[1];

    const startHour = startMins >= 60 ? Math.floor(startMins / 60) : 0;
    const startDay = startHour >= 24 ? Math.floor(startHour / 24) : 0;
    const endHour = endMins >= 60 ? Math.floor(endMins / 60) + 1 : 0;
    const endDay = endHour >= 24 ? Math.floor(endMins / 60 / 24) : 0;

    const startTime = new Date();
    startTime.setHours(startHour, 0, 0);
    const endTime = new Date();
    endTime.setHours(endHour, 0, 0);

    result.push({
      startDay,
      endDay,
      startTime,
      endTime,
    });
  }
  return result;
}

// Helper function to get filter key for time
function getFilterKeyForTime(object: Record<string, any>): string | undefined {
  if (object?.all && Object.keys(object?.all).length) {
    return Object.keys(object?.all)[0];
  }
  if (object?.none && Object.keys(object?.none).length) {
    return Object.keys(object?.none)[0];
  }
}

// Extracted getValuesForTime function
export function getValuesForTime(
  content: Record<string, any> | null | undefined
) {
  const key = content?.time ? getFilterKeyForTime(content.time) : null;
  const defaultValues = targetExpressionModuleDefaultValues();
  if (!key) return defaultValues.time.timeOfWeek;

  const timeValues = valueForIncludeOrExcludeFilter<TimeResponseResult[]>(
    content,
    "time",
    key,
    timeResponseMapper as any
  );

  if (!timeValues.type && !timeValues.value.length)
    return defaultValues.time.timeOfWeek;

  return { key, ...timeValues };
}

// Content mapping function extracted from defaultValuesForEdit
export function mapTargetingExpressionContent(
  content: Record<string, any> | null | undefined
) {
  return {
    app_site: {
      app_bundle_list: {
        value: valueForIncludeAndExcludeFilter(
          content,
          "app_site",
          "app_bundle_list"
        ),
      },
      app_id_list: {
        value: valueForIncludeAndExcludeFilter(
          content,
          "app_site",
          "app_id_list"
        ),
      },
      deal_id_list: {
        value: valueForIncludeAndExcludeFilter(
          content,
          "app_site",
          "deal_id_list"
        ),
      },
      deal_id: {
        value: valueForIncludeAndExcludeFilter(content, "app_site", "deal_id"),
      },
      app_name: {
        value: valueForIncludeAndExcludeFilter(content, "app_site", "app_name"),
      },
      domain_list: {
        value: valueForIncludeAndExcludeFilter(
          content,
          "app_site",
          "domain_list"
        ),
      },
    },
    environment: {
      ad_position: valueForIncludeOrExcludeFilter(
        content,
        "environment",
        "ad_position",
        mapForFilterValuesAndParseAsString
      ),
      environment_type: valueForIncludeOrExcludeFilter(
        content,
        "environment",
        "environment_type",
        mapForFilterValuesAndParseAsString
      ),
    },
    exchange: {
      inventory_source: valueForIncludeOrExcludeFilter(
        content,
        "exchange",
        "inventory_source"
      ),
    },
    geo: {
      country: valueForIncludeOrExcludeFilter(content, "geo", "country"),
      region: valueForIncludeOrExcludeFilter(content, "geo", "region"),
      city: valueForIncludeOrExcludeFilter(content, "geo", "city"),
      zip_code_list: {
        value: valueForIncludeAndExcludeFilter(content, "geo", "zip_code_list"),
      },
    },
    platform: {
      device_type: valueForIncludeOrExcludeFilter(
        content,
        "platform",
        "device_type"
      ),
    },
    time: {
      timeOfWeek: content
        ? getValuesForTime(content)
        : targetExpressionModuleDefaultValues().time.timeOfWeek,
    },
    user: {
      segment: {
        value: reverseMapForSegment(content, "user", "segment"),
      },
      custom_segment: {
        value: {
          segmentIds: getCustomSegmentsFromSegments(content) ?? [],
        },
      },
    },
  };
}

// Helper functions for segment mapping
const VERB_USAGE = {
  INCLUDE: "INCLUDE",
  EXCLUDE: "EXCLUDE",
  REQUIRE: "REQUIRE",
} as const;

type VerbUsageType = (typeof VERB_USAGE)[keyof typeof VERB_USAGE];

function mapForSegment(
  data: FilterValue[] | null | undefined,
  type: string
): Map<string | number, VerbUsageType> {
  if (!data) return new Map();
  const requiredType =
    type === "exclude"
      ? VERB_USAGE.EXCLUDE
      : type === "require"
      ? VERB_USAGE.REQUIRE
      : VERB_USAGE.INCLUDE;
  const requiredData = data.map((item) => {
    return [item.value, requiredType] as [string | number, VerbUsageType];
  });
  return new Map(requiredData);
}

function reverseMapForSegment(
  data: Record<string, any> | null | undefined,
  module: string,
  filter: string
) {
  return {
    included: mapForSegment(
      filterCustomSegmentsFromSegments(
        valueForReverseMap(data, module, filter, "include")
      ),
      "include"
    ),
    excluded: mapForSegment(
      valueForReverseMap(data, module, filter, "exclude"),
      "exclude"
    ),
    required: mapForSegment(
      valueForReverseMap(data, module, filter, "require"),
      "require"
    ),
  };
}
function getCustomSegmentsFromSegments(
  data: Record<string, any> | null | undefined
) {
  const segments = valueForReverseMap(data, "user", "segment", "include");
  if (!segments) return segments;
  const customSegments = segments.filter(({ value }: Record<string, any>) =>
    value.includes(CUSTOM_SEGMENT_KEY_PREFIX)
  );
  return customSegments.map(({ value }: Record<string, any>) =>
    Number(value.split("-")[1])
  );
}
function filterCustomSegmentsFromSegments(
  values: FilterValue[] | null | undefined
) {
  if (!values) return values;
  return values.filter(
    ({ value }: Record<string, any>) =>
      !value.includes(CUSTOM_SEGMENT_KEY_PREFIX)
  );
}

// An audience id is only unique within its own table, and the "Site Visitor" list
// mixes retarget audiences with Visitor Insight ones, so the picker holds
// "<audienceType>::<id>" instead of a bare id.
export const AUDIENCE_REF_SEPARATOR = "::";

export function toAudienceRefValue(
  audienceType: string,
  id: number | string
): string {
  return `${audienceType}${AUDIENCE_REF_SEPARATOR}${id}`;
}

export function parseAudienceRefValue(value: number | string): {
  id: number;
  audienceType: string | null;
} {
  if (typeof value === "number") return { id: value, audienceType: null };
  const separatorIndex = value.lastIndexOf(AUDIENCE_REF_SEPARATOR);
  // A bare id — a line item saved before the picker moved to composite values.
  if (separatorIndex === -1) return { id: Number(value), audienceType: null };
  return {
    id: Number(value.slice(separatorIndex + AUDIENCE_REF_SEPARATOR.length)),
    audienceType: value.slice(0, separatorIndex),
  };
}

export function parseAudienceRefValues(
  values: (number | string)[] | undefined | null
): { id: number; audienceType: string | null }[] {
  return (values ?? [])
    .map(parseAudienceRefValue)
    .filter((ref) => !Number.isNaN(ref.id));
}

export function getSegmentIdsFromAudience({
  ids,
  data,
}: {
  ids: (number | string)[];
  data: Record<string, any>[];
}) {
  const segmentIds = new Set();
  ids.forEach((value) => {
    const ref = parseAudienceRefValue(value);
    const audience = data?.find((item) => {
      // Fall back to the id alone for rows the list didn't annotate with a type.
      if (ref.audienceType && item?.audienceRefType)
        return item.id === ref.id && item.audienceRefType === ref.audienceType;
      return item?.id === ref.id;
    });
    if (audience) {
      // Retarget audiences keep their one pixel segment on the row itself.
      if (audience.segmentId) {
        segmentIds.add(audience.segmentId);
      }
      if (audience.bidSegmentId) {
        segmentIds.add(audience.bidSegmentId);
      }
      // criteria is free-form JSON and Visitor Insight rows have no regions key.
      const regions = audience.criteria?.regions ?? [];
      if (
        audience.IPSegmentId &&
        (regions.includes("APAC") || regions.includes("EMEA"))
      ) {
        segmentIds.add(audience.IPSegmentId);
      }
      // Visitor Insight audiences hold their segment in BeeswaxSegments instead.
      (audience.BeeswaxSegments ?? []).forEach((segment: any) => {
        if (segment?.segmentId) segmentIds.add(segment.segmentId);
      });
    }
  });
  return Array.from(segmentIds);
}
